/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React from "react";

import { Coordinates, DEFAULT_PAGE_HEIGHT, getBestSpotForBlock, PlateSnapshot } from "@vienna/common";

import { API } from "src/classes/api";
import { Block } from "src/classes/block";
import { BaseBlock } from "src/classes/block/BaseBlock";
import { ConvertibleBlockType, FileBlock, ImageBlock, TextBlock } from "src/classes/block/block-types";
import { BlockFactory } from "src/classes/block/BlockFactory";
import { ClipboardManager } from "src/classes/plate/clipboard/ClipboardManager";
import { CreateCommand, DeleteCommand, SelectCommand } from "src/classes/plate/Command";
import { Key, KeyboardManager, KeyEventType, KeyHandler } from "src/classes/plate/Keyboard";
import { Direction, Point, Position, Size } from "src/classes/plate/lib/types";
import { updateEditors, updateTitle } from "src/classes/plate/lib/utils";
import { MouseTracker } from "src/classes/plate/Mouse/MouseTracker";
import { SessionType } from "src/classes/plate/Session";
import { ClickSession } from "src/classes/plate/Session/BaseSession";
import { SessionManager } from "src/classes/plate/Session/SessionManager";
import { BaseUser } from "src/classes/user";
import { MathUtils } from "src/classes/utilities/MathUtils";
import { PositionUtils } from "src/classes/utilities/PositionUtils";
import { Toaster } from "src/classes/utilities/Toaster";
import { DEFAULT_FILE_BLOCK_HEIGHT, DEFAULT_FILE_BLOCK_WIDTH, ONE_GRID_UNIT } from "src/lib/constants";

import { BasePlate } from "./BasePlate/BasePlate";
import { PlateGenerator } from "./Generator/PlateGenerator";
import { PlateSocket } from "./PlateSocket/PlateSocket";

export class Plate extends BasePlate<PlateSnapshot> {
  /**
   * Tracks and responds to click events.
   */
  clickSession: ClickSession | null = null;

  keyboardManager: KeyboardManager;

  mouseTracker: MouseTracker; // @todo? add socket mice? they are currently managed by the socket class

  clipboard: ClipboardManager;

  socket: PlateSocket;

  sessionManager: SessionManager;

  blocks: Block[];

  editors: BaseUser[];

  owner: BaseUser;

  generator: PlateGenerator | null;

  private constructor(snapshot: PlateSnapshot) {
    super(snapshot);
    this.socket = PlateSocket.create(this);

    this.blocks = snapshot.initialBlockSnapshots.map((blockSnapshot) =>
      BlockFactory.create(blockSnapshot, this.socket)
    );

    const keyHandlers = [new KeyHandler(Key.Escape, this.handleEscape.bind(this), KeyEventType.Down)];
    this.keyboardManager = new KeyboardManager(keyHandlers);

    this.mouseTracker = MouseTracker.create(this);

    this.clipboard = new ClipboardManager(this);

    this.sessionManager = SessionManager.create(this);

    this.editors = snapshot.initialEditors.map((editor) => BaseUser.fromBackendUser(editor));

    this.owner = BaseUser.fromBackendUser(snapshot.owner);

    this.generator = this.snapshot.editable ? PlateGenerator.create(this) : null;
  }

  /* -------------------------------------------------- */
  /*                    Mouse Events                    */
  /* -------------------------------------------------- */
  public onMouseDown(e: React.MouseEvent<HTMLDivElement, MouseEvent>): void {
    if (e.button !== 0) return;
  }

  public onMouseUp(): void {
    // @later this jank is an attempt to fix "sticky cursor" bug
    if (
      this.sessionManager.currentSession &&
      this.sessionManager.currentSession.snapshot.type !== SessionType.Arrow
    ) {
      this.sessionManager.endSession();
    }
  }

  public onMouseMove(e: MouseEvent): void {
    if (this.snapshot.editable) {
      this.mouseTracker.handleMouseMove(e);
      this.socket.sendMouseWithThrottle(this.mouseTracker.snapshot.position);
    }
  }

  public onMouseUpBlock(block: Block): void {
    const clickSession = this.clickSession;
    this.clickSession = null;

    const mouseHasMoved = this.mouseTracker.getHasMovedFromPoint(clickSession?.anchor);
    // enforce one selection per click & don't change selection on drag events
    if (clickSession?.hasMadeSelection || mouseHasMoved) {
      return;
    }

    const isAlreadySelected = this.snapshot.selection.includes(block.snapshot.id);
    const multipleSelected = this.snapshot.selection.length > 1;

    const isDeselecting = this.keyboardManager.isKeyPressed(Key.Shift);

    if (isDeselecting && isAlreadySelected) {
      this.deselect([block.snapshot.id]); // deselect if shiftKey and block was selected
    } else if (multipleSelected) {
      this.deselect(this.snapshot.selection.filter((id) => id !== block.snapshot.id)); // deselect all but the one you clicked on
    }
  }

  public onMouseDownBlock(block: Block, e: React.MouseEvent): void {
    if (!this.snapshot.editable) return;
    if (e.button !== 0) return;
    e.stopPropagation();
    if (!this.mouseTracker.snapshot.position.x || !this.mouseTracker.snapshot.position.y) {
      this.mouseTracker.updateMousePositionFromEvent(e); // set mouse position if it hasn't been set yet (there was a mouseDown before any mouseMove, eg after reload)
    }
    const isSelected = this.snapshot.selection.includes(block.snapshot.id);
    this.clickSession = { anchor: this.mouseTracker.snapshot.position, hasMadeSelection: false };

    //z-index management
    const { value: maxZIndex, occurrences } = this.highestZIndex;
    const newZIndex = block.snapshot.zIndex === maxZIndex && occurrences === 1 ? null : maxZIndex + 1;
    if (newZIndex) {
      block.changeZIndex(newZIndex);
    }
    if (isSelected) {
      return;
    }

    const isDoingMultiselect = this.keyboardManager.isKeyPressed(Key.Shift);
    if (isDoingMultiselect) {
      this.select([...this.snapshot.selection, block.snapshot.id]); // add to selection
      this.clickSession.hasMadeSelection = true;
    } else {
      this.select([block.snapshot.id]); // replace selection
      this.clickSession.hasMadeSelection = true;
    }
  }

  public onDragBlockStart(block: Block, e: React.MouseEvent): void {
    this.onMouseDownBlock(block, e);
    this.sessionManager.startSession(SessionType.DragBlock);
  }

  public onMouseDownCanvas(e: React.MouseEvent): void {
    if (!this.mouseTracker.snapshot.position.x || !this.mouseTracker.snapshot.position.y) {
      this.mouseTracker.updateMousePositionFromEvent(e); // set mouse position if it hasn't been set yet (there was a mouseDown before any mouseMove, eg after reload)
    }

    this.deselectAll();
    this.sessionManager.startSession(SessionType.DragCanvas);
  }

  public cleanupBlocks = (): void => {
    const blocksSortedBySize = PositionUtils.sortBlocksBySize(this.blocks);
    const repositionedBlocks: Coordinates[] = [];
    // let newPageHeight = this.height;
    blocksSortedBySize.map((block) => {
      const random = Math.random() < 0.5;
      const { top, left, width, height } = getBestSpotForBlock({
        positionedBlocks: repositionedBlocks,
        naiveCoordinates: {
          top: block.snapshot.top,
          left: block.snapshot.left,
          width: block.snapshot.width,
          height: block.snapshot.height,
        },
        goLeft: random,
      });
      repositionedBlocks.push({ top, left, width, height });
      block.move(top, left);
      const newPageHeight = Math.max(
        this.snapshot.height,
        this.snapshot.height * Math.ceil((height + top + 10) / this.snapshot.height) // extends page by multiple of the default height
      ); // check if page needs to be taller
      if (newPageHeight !== this.snapshot.height) {
        this.changeHeight(newPageHeight);
      }
    });
  };

  /**
   * Nudges selected blocks
   *
   * @param delta - the amount to move the selected blocks by
   * @param direction - the direction to move the selected blocks in
   */
  private nudge = (delta: number, direction: Direction): void => {
    if (!this.snapshot.selection.length) return;

    const maxMove = PositionUtils.getMaxMoveForBlocks(
      this.selectedBlocks.map((b) => b.snapshot),
      this.dimensions
    )[direction];

    const isNegative = ["left", "up"].includes(direction);

    const dx = (["left", "right"].includes(direction) ? Math.min(delta, maxMove) : 0) * (isNegative ? -1 : 1);
    const dy = (["up", "down"].includes(direction) ? Math.min(delta, maxMove) : 0) * (isNegative ? -1 : 1);

    this.selectedBlocks.forEach((block) => {
      block.move(block.snapshot.top + dy, block.snapshot.left + dx);
    });
  };

  /* ----------------- Resize ---------------- */

  public onResizeStart(block: Block, e: React.MouseEvent): void {
    this.onMouseDownBlock(block, e);
    this.sessionManager.startSession(SessionType.Resize);
  }

  /* ----------------- Block CRUD ---------------- */

  public createBlock = (newBlock: Block, undoable: boolean = true, needToSave: boolean = true): void => {
    this.createBlocks([newBlock], undoable, needToSave);
  };

  public createBlocks = (newBlocks: Block[], undoable: boolean = true, needToSave: boolean = true): void => {
    const cmd = undoable ? new CreateCommand(newBlocks, this) : null;
    this.blocks = [...this.blocks, ...newBlocks];
    this.setState({ blockIds: this.blockIds }, cmd);
    if (needToSave) {
      this.socket.createBlocks(newBlocks.map((b) => b.snapshot));
    }
  };

  public repealAndReplaceTextBlock = (textBlock: TextBlock, newType: ConvertibleBlockType): void => {
    const newSnapshot = TextBlock.convertSnapshot(textBlock.snapshot, newType);
    const newBlock = BlockFactory.create(newSnapshot, this.socket);
    this.deleteBlock(textBlock.snapshot.id);
    this.createBlock(newBlock);
  };

  public deleteBlock = (blockId: string, undoable: boolean = true, needToSave: boolean = true): void => {
    this.deleteBlocks([blockId], undoable, needToSave);
  };

  public deleteBlocks = (blockIds: string[], undoable: boolean = true, needToSave = true): void => {
    const deletedBlocks = this.blocks.filter((block) => blockIds.includes(block.snapshot.id));
    const deletedIds = deletedBlocks.map((block) => block.snapshot.id);
    const cmd = undoable ? new DeleteCommand(deletedBlocks, this) : null;
    this.blocks = this.blocks.filter((block) => !deletedIds.includes(block.snapshot.id));
    this.deselect(deletedIds);
    this.setState({ blockIds: this.blockIds }, cmd);
    if (needToSave) {
      this.socket.deleteBlocks(deletedIds);
    }
  };

  private get blockIds(): string[] {
    return this.blocks.map((block) => block.snapshot.id);
  }

  /* ----------------- Miscellaneous ---------------- */

  public changeTitle = (title: string): Promise<void> => {
    return updateTitle({
      id: this.snapshot.id,
      username: this.snapshot.owner.username,
      title,
    }).then(({ succeeded }) => {
      if (!succeeded) return;
      this.setState({ title });
    });
  };

  public extend = (): void => {
    this.changeHeight(this.snapshot.height + DEFAULT_PAGE_HEIGHT);
  };

  public changeHeight = async (height: number): Promise<void> => {
    try {
      await API.plates.update(this.snapshot.id, { height });
      this.setState({ height });
    } catch (e) {
      Toaster.error("Error updating plate.");
    }
  };

  public addEditor = async (editor: BaseUser): Promise<void> => {
    const { succeeded } = await updateEditors({
      plateId: this.snapshot.id,
      editorId: editor.snapshot.id,
      action: "ADD",
    });
    if (succeeded) {
      this.editors = [...this.editors, editor];
      this.setState({ initialEditors: [...this.snapshot.initialEditors] }); //note: hack to force rerender
    }
  };

  public removeEditor = async (editor: BaseUser): Promise<void> => {
    const { succeeded } = await updateEditors({
      plateId: this.snapshot.id,
      editorId: editor.snapshot.id,
      action: "REMOVE",
    });
    if (succeeded) {
      this.editors = this.editors.filter(({ snapshot }) => snapshot.id !== editor.snapshot.id);
      this.setState({ initialEditors: [...this.snapshot.initialEditors] }); //note: hack to force rerender
    }
  };

  public onDrop = (files: File[], e: React.DragEvent<Element> | any): void => {
    this.mouseTracker.updateMousePositionFromEvent(e);
    files.forEach((file, index) => this.onDropFile(file, index));
  };

  public onDropFile = (file: File, index: number): void => {
    const block = this.makeBlockFromFile(file, index);
    this.createBlocks([block]);
    block.addFile(file, this.dimensions);
  };

  private makeBlockFromFile = (file: File, index: number): FileBlock | ImageBlock => {
    const width = DEFAULT_FILE_BLOCK_WIDTH;
    const height = DEFAULT_FILE_BLOCK_HEIGHT;
    const offset = { x: index * 10, y: index * 10 };
    const { top, left } = this.positionBlockFromMouseLocation(width, height, offset);
    const baseArgs = { top, left, width, height, zIndex: this.highestZIndex.value + 1, plateId: this.snapshot.id };
    const baseSnapshot = BaseBlock.newBaseBlockSnapshot(baseArgs);
    const isImage = file.type.match("image.*");
    const snapshot = isImage ? ImageBlock.newSnapshot(baseSnapshot) : FileBlock.newSnapshot(baseSnapshot);
    return BlockFactory.create(snapshot, this.socket) as FileBlock | ImageBlock;
  };

  private positionBlockFromMouseLocation = (
    blockWidth: number,
    blockHeight: number,
    offsetBy: Point
  ): Position => {
    const naiveLeft = this.mouseTracker.snapshot.position.x - offsetBy.x;
    const maxLeft = this.snapshot.width - blockWidth;
    const left = MathUtils.constrainValueToBounds(naiveLeft, 0, maxLeft);

    const naiveTop = this.mouseTracker.snapshot.position.y - offsetBy.y;
    const maxTop = this.snapshot.height - blockHeight;
    const top = MathUtils.constrainValueToBounds(naiveTop, 0, maxTop);

    return { top, left };
  };

  public showModal(modal: { title: string; body: React.ReactNode }): void {
    this.setState({ modal });
  }

  public closeModal(): void {
    this.setState({ modal: null });
  }

  /* ----------------- Selection ---------------- */

  public get selectedBlocks(): Block[] {
    return this.snapshot.selection.map((id) => this.getBlockById(id)).filter((exists) => exists) as Block[];
  }

  public getBlockById(id: string): Block | null {
    return this.blocks.find((block) => block.snapshot.id === id) || null;
  }

  public select = (selection: string[], { addToStack }: { addToStack: boolean } = { addToStack: true }): void => {
    const lockedBlockIds = this.socket.snapshot.lockedBlocks.map(({ blockId: id }) => id);
    const newSelection = selection.filter((id) => !lockedBlockIds.includes(id));
    this._modifySelection(newSelection, { addToStack });
  };

  public selectAll = ({ addToStack }: { addToStack: boolean } = { addToStack: true }): void => {
    if (this.blocks.length === this.snapshot.selection.length) return;
    const lockedBlockIds = this.socket.snapshot.lockedBlocks.map(({ blockId: id }) => id);
    const newSelection = this.blocks
      .map((block) => block.snapshot.id)
      .filter((id) => !lockedBlockIds.includes(id));
    this._modifySelection(newSelection, { addToStack });
  };

  public deselect = (blockIds: string[], { addToStack }: { addToStack: boolean } = { addToStack: true }): void => {
    const newSelection = this.snapshot.selection.filter((id) => !blockIds.includes(id));
    this._modifySelection(newSelection, { addToStack });
  };

  public deselectAll = ({ addToStack }: { addToStack: boolean } = { addToStack: true }): void => {
    if (!this.snapshot.selection.length) return;
    this._modifySelection([], { addToStack });
  };

  private _modifySelection = (newSelection: string[], { addToStack }: { addToStack: boolean }): void => {
    if (!this.snapshot.editable) return;
    const cmd = addToStack ? new SelectCommand(this.snapshot.selection, newSelection, this) : null;
    // must come before selection is updated
    const oldSelection = this.snapshot.selection;
    this.socket.emitSelectionChange(newSelection, oldSelection);
    this.setState({ selection: newSelection }, cmd);
  };

  public get highestZIndex(): { value: number; occurrences: number } {
    const value = this.blocks.length ? Math.max(...this.blocks.map((block) => block.snapshot.zIndex)) : 0;
    const occurrences = this.blocks.filter((block) => block.snapshot.zIndex === value).length;
    return { value, occurrences };
  }

  /* -------------------------------------------------- */
  /*                   Key Callbacks                    */
  /* -------------------------------------------------- */
  public handleEscape = (): void => {
    if (this.sessionManager.currentSession) {
      this.sessionManager.cancelSession();
    }
  };

  // this is called via the usePlateShortcuts() hook, which is kind of doing the same thing as KeyboardManager and we should merge them
  public onArrowKey = (direction: Direction): void => {
    if (!this.snapshot.editable || this.editingText) return;
    if (!this.sessionManager.currentSession) {
      this.sessionManager.startSession(SessionType.Arrow);
    }
    const isBigMove = this.keyboardManager.isKeyPressed(Key.Shift);
    this.nudge(ONE_GRID_UNIT * (isBigMove ? 5 : 1), direction);
    this.sessionManager.debouncedEndSession();
  };

  public onBackspace(): void {
    if (!this.snapshot.editable || this.editingText) return;
    if (this.snapshot.selection.length) {
      this.deleteBlocks(this.snapshot.selection);
    }
  }

  /* ------------------- UNDO/REDO ------------------ */

  public onUndo(): void {
    if (!this.snapshot.editable || this.editingText) return;
    this.sessionManager.flushPendingStackWrites();
    this.undo();
  }

  public onRedo(): void {
    if (!this.snapshot.editable || this.editingText) return;
    this.redo();
  }

  /* -------------------------------------------------- */
  /*                       Utils                        */
  /* -------------------------------------------------- */
  public get dimensions(): Size {
    return { width: this.snapshot.width, height: this.snapshot.height };
  }

  public get blockCount(): number {
    return this.blocks.length;
  }

  public get editingText(): boolean {
    if (document.activeElement?.hasAttribute("contenteditable")) return true;
    if (["TEXTAREA", "INPUT"].includes(document.activeElement?.tagName || "")) return true;
    return false;
  }

  /* -------------------------------------------------- */
  /*                      Destroy                       */
  /* -------------------------------------------------- */
  public destroy(): void {
    this.socket.destroy();
    this.keyboardManager.destroy();
    this.generator?.destroy();
  }

  /* -------------------------------------------------- */
  /*                  Static Methods                    */
  /* -------------------------------------------------- */
  public static async load(username: string, title: string): Promise<Plate> {
    const plate = await API.plates.get(username, title);
    return plate;
  }

  public static async create(title?: string): Promise<Plate | undefined> {
    const plate = await API.plates.create({ title });
    return plate;
  }

  public static fromSnapshot(snapshot: PlateSnapshot): Plate {
    return new Plate(snapshot);
  }
}
